import { CreateTokenResponse, parseMollieResponse, TokenCreationError } from './mollie-response-parser.js'
import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'


export interface ThirdPartyMollieApi {
  createToken(): Promise<CreateTokenResponse>
  initialize(profileId: string, locale: string, testMode: boolean, element: HTMLElement): void
}


export const THIRD_PARTY_MOLLIE_API = Symbol('THIRD_PARTY_MOLLIE_API')

@registerDependency(FAKE, THIRD_PARTY_MOLLIE_API)
export class FakeMollieApi implements ThirdPartyMollieApi {
  public static fakeToken = 'fake-token'
  public static fakeProfileId = 'fake-profile-id'

  private initialized = false
  private willFail = false
  public seenLocales: string[] = []
  public testMode?: boolean = undefined

  initialize(profileId: string, locale: string, testMode: boolean, element: HTMLElement): void {
    if (element === null) {
      throw new Error('element is null')
    }
    if (profileId !== FakeMollieApi.fakeProfileId) {
      throw new Error('something wrong with profile id')
    }
    this.testMode = testMode
    this.seenLocales.push(locale)
    this.initialized = true
  }

  async createToken(): Promise<CreateTokenResponse> {
    if (!this.initialized) {
      return new TokenCreationError('you should initialize before calling createToken')
    }
    if (this.willFail) {
      return new TokenCreationError('token creation failed')
    }
    return FakeMollieApi.fakeToken
  }

  failTokenCreation(): void {
    this.willFail = true
  }
}

@registerDependency(REAL, THIRD_PARTY_MOLLIE_API)
export class RealMollieApi implements ThirdPartyMollieApi {
  private mollie: any = null
  private mounting: Promise<void> = Promise.resolve()

  async createToken(): Promise<CreateTokenResponse> {
    await this.mounting
    const mollieResponse = await this.mollie.createToken()
    return parseMollieResponse(mollieResponse)
  }

  initialize(profileId: string, locale: string, testMode: boolean, element: HTMLElement): void {
    this.mounting = this.mount(profileId, locale, testMode, element)
  }

  private async mount(profileId: string, locale: string, testMode: boolean, element: HTMLElement): Promise<void> {
    const mollieConstructor = await waitForMollie()
    this.mollie = mollieConstructor(profileId, { locale: locale, testmode: testMode })
    const component = this.mollie.createComponent('card')
    component.mount(element)
  }
}

type MollieConstructor = (profileId: string, options: object) => any

export const waitForMollie = async (): Promise<MollieConstructor> => {
  if (document.readyState === 'loading') {
    await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve, { once: true }))
  }
  const mollieConstructor = (window as unknown as { Mollie?: MollieConstructor }).Mollie
  if (mollieConstructor === undefined) {
    throw new Error('Mollie script failed to load')
  }
  return mollieConstructor
}
